/**
 * Client Care Letter body editor: a plain textarea plus a small
 * formatting toolbar that inserts the lightweight markup understood by
 * <CclBody> and the backend PDF renderer:
 *   Bold      → wraps the selection in **…**
 *   Heading   → prefixes the current line with "## "
 *   Bullets   → prefixes each selected line with "- "
 */
import { useRef } from "react";
import { TextB, ListBullets, TextHOne } from "@phosphor-icons/react";

function ToolbarButton({ onClick, title, testid, children }) {
  return (
    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick}
      title={title} data-testid={testid}
      className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-slate-200 bg-white text-slate-600 text-[11px] font-medium hover:bg-slate-50 hover:text-slate-900 transition-colors">
      {children}
    </button>
  );
}

export default function CclEditor({ value, onChange, disabled, testid, heightClass = "h-[420px]" }) {
  const ref = useRef(null);

  const apply = (fn) => {
    const el = ref.current;
    if (!el) return;
    const start = el.selectionStart ?? value.length;
    const end = el.selectionEnd ?? value.length;
    const { next, selStart, selEnd } = fn(value, start, end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(selStart, selEnd);
    });
  };

  const wrapBold = () => apply((text, start, end) => {
    const sel = text.slice(start, end) || "bold text";
    const next = `${text.slice(0, start)}**${sel}**${text.slice(end)}`;
    return { next, selStart: start + 2, selEnd: start + 2 + sel.length };
  });

  const prefixLine = (marker) => apply((text, start, end) => {
    const lineStart = text.lastIndexOf("\n", start - 1) + 1;
    const lineEnd = text.indexOf("\n", end);
    const stop = lineEnd === -1 ? text.length : lineEnd;
    const block = text.slice(lineStart, stop);
    const prefixed = block
      .split("\n")
      .map((l) => (l.startsWith(marker) ? l : marker + l))
      .join("\n");
    const next = text.slice(0, lineStart) + prefixed + text.slice(stop);
    return { next, selStart: lineStart, selEnd: lineStart + prefixed.length };
  });

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2 flex-wrap" data-testid={`${testid}-toolbar`}>
        <ToolbarButton onClick={wrapBold} title="Bold (**text**)" testid={`${testid}-bold`}>
          <TextB weight="bold" className="w-3.5 h-3.5" /> Bold
        </ToolbarButton>
        <ToolbarButton onClick={() => prefixLine("## ")} title="Heading (## )" testid={`${testid}-heading`}>
          <TextHOne weight="bold" className="w-3.5 h-3.5" /> Heading
        </ToolbarButton>
        <ToolbarButton onClick={() => prefixLine("- ")} title="Bullet list (- )" testid={`${testid}-bullet`}>
          <ListBullets weight="bold" className="w-3.5 h-3.5" /> Bullets
        </ToolbarButton>
        <span className="text-[10px] text-slate-400 ml-1">Use <code>**bold**</code>, <code># Heading</code>, <code>- bullet</code></span>
      </div>
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck
        disabled={disabled}
        className={`w-full ${heightClass} rounded-md border border-slate-300 p-3 font-mono text-[12px] leading-relaxed text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#100f5d]/30 disabled:opacity-60`}
        data-testid={testid}
      />
    </div>
  );
}
